import axios, { 
  AxiosInstance, 
  AxiosRequestConfig, 
  AxiosError,
  InternalAxiosRequestConfig 
} from 'axios';
import { useAuthStore } from '../store';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.dev.crescendosofttech.com/api/tally-service';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add JWT token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = useAuthStore.getState().token;
        
        if (import.meta.env.DEV) {
          console.log('🌐 API Request:', {
            url: config.url,
            method: config.method,
            hasToken: !!token,
            tokenLength: token?.length || 0
          });
        }
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (import.meta.env.DEV) {
          console.warn('⚠️ No token available for API request');
        }

        // Add request timestamp for debugging
        config.metadata = { startTime: Date.now() };
        
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        // Calculate response time for monitoring
        const startTime = response.config.metadata?.startTime;
        if (startTime) {
          const duration = Date.now() - startTime;
          console.debug(`API Call: ${response.config.method?.toUpperCase()} ${response.config.url} took ${duration}ms`);
        }

        // Check if response has success: false in the body
        if (response.data && typeof response.data === 'object' && response.data.success === false) {
          // Convert success: false responses into errors so they're handled consistently
          const error = new Error(response.data.message || 'API request failed');
          return Promise.reject(error);
        }

        return response;
      },
      (error: AxiosError) => {
        const { response } = error;

        // Handle different error scenarios
        if (response?.status === 401) {
          // Unauthorized - token expired or invalid
          useAuthStore.getState().logout();
          return Promise.reject(new Error('Your session has expired. Please close and reopen the app.'));
        }

        if (response?.status === 403) {
          // Forbidden - insufficient permissions
          return Promise.reject(new Error('You do not have permission to access this resource.'));
        }

        if (response && response.status >= 500) {
          // Server errors
          return Promise.reject(new Error('Server is temporarily unavailable. Please try again later.'));
        }

        if (!response) {
          // Network errors
          return Promise.reject(new Error('Network connection failed. Please check your internet connection.'));
        }

        // API error responses
        if (response.data && typeof response.data === 'object' && 'message' in response.data && typeof response.data.message === 'string') {
          return Promise.reject(new Error(response.data.message));
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // GET request
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  // POST request
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  // PUT request
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  // DELETE request
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // Check if user is online
  isOnline(): boolean {
    return navigator.onLine;
  }

  // Get current auth token
  getToken(): string | null {
    return useAuthStore.getState().token;
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return useAuthStore.getState().isAuthenticated;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Extend AxiosRequestConfig to include metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}